/**
 * useCreditEstimate - React hook for real-time credit estimation
 *
 * Features:
 * - Debounced API calls to prevent excessive requests
 * - Automatic re-estimation when params change
 * - Loading state management
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import {
  CreditEstimateScenario,
  CreditEstimateResult,
  estimateCredits,
  formatEstimatedCredits,
} from '@/services/creditEstimateService'

export interface UseCreditEstimateInput {
  /** Whether estimation is enabled */
  enabled: boolean
  /** The scenario to estimate */
  scenario: CreditEstimateScenario
  /** Scenario-specific parameters */
  params: Record<string, any>
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number
}

export interface UseCreditEstimateResult {
  /** Whether estimation is in progress */
  estimating: boolean
  /** Estimated credit range (min/max) */
  estimatedRange: { min: number; max: number } | null
  /** Minimum balance required to proceed */
  requiredMinimumBalance: number | null
  /** Formatted display string (e.g., "~50-200") */
  formattedEstimate: string | null
  /** Error if estimation failed */
  error: Error | null
  /** Manually trigger re-estimation */
  refresh: () => void
}

export function useCreditEstimate(
  input: UseCreditEstimateInput
): UseCreditEstimateResult {
  const { enabled, scenario, params, debounceMs = 300 } = input

  const [estimating, setEstimating] = useState(false)
  const [result, setResult] = useState<CreditEstimateResult | null>(null)
  const [error, setError] = useState<Error | null>(null)

  // Memoize params to detect changes
  const paramsKey = useMemo(() => JSON.stringify(params), [params])

  // Debounced estimation function
  const doEstimate = useCallback(async () => {
    if (!enabled) {
      setResult(null)
      return
    }

    setEstimating(true)
    setError(null)

    try {
      const res = await estimateCredits({ scenario, params })
      setResult(res)
    } catch (err) {
      console.error('Credit estimation failed:', err)
      setError(err as Error)
      setResult(null)
    } finally {
      setEstimating(false)
    }
  }, [enabled, scenario, paramsKey])

  const debouncedEstimate = useDebouncedCallback(doEstimate, debounceMs)

  // Trigger estimation when inputs change
  useEffect(() => {
    if (enabled) {
      debouncedEstimate()
    } else {
      setResult(null)
      setError(null)
    }
  }, [enabled, scenario, paramsKey, debouncedEstimate])

  // Compute derived values
  const estimatedRange = useMemo(() => {
    if (!result) return null
    return {
      min: result.estimated_min_credits,
      max: result.estimated_max_credits,
    }
  }, [result])

  const formattedEstimate = useMemo(() => {
    if (!estimatedRange) return null
    return formatEstimatedCredits(estimatedRange.min, estimatedRange.max)
  }, [estimatedRange])

  return {
    estimating,
    estimatedRange,
    requiredMinimumBalance: result?.required_minimum_balance ?? null,
    formattedEstimate,
    error,
    refresh: doEstimate,
  }
}

/**
 * Simplified hook for single-use estimation
 * Returns just the formatted estimate string
 */
export function useSimpleCreditEstimate(
  scenario: CreditEstimateScenario,
  count: number,
  enabled: boolean = true
): { estimate: string | null; loading: boolean } {
  const { formattedEstimate, estimating } = useCreditEstimate({
    enabled,
    scenario,
    params: { count },
    debounceMs: 250,
  })

  return {
    estimate: formattedEstimate,
    loading: estimating,
  }
}
